import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { DesignSite } from '@freedom/shared';
import type { ElevationProfile } from '@freedom/elevation';
import type { LosDiagnostics } from '@freedom/rf-engine';
import { haversineDistanceM } from '@freedom/geospatial';
import {
  createProfileScale,
  nearestProfileSampleIndex,
  profileHoverTarget,
  profileTickValues,
  profileTooltipPlacement,
  profileXAtDistance,
  profileYAtElevation,
  type ProfileScale,
} from './profile-geometry';

function drawPath<Sample extends { distanceM: number }>(context: CanvasRenderingContext2D, samples: readonly Sample[], x: (sample: Sample) => number, y: (value: number) => number, value: (sample: Sample) => number): void {
  context.beginPath();
  samples.forEach((sample, index) => {
    const pointX = x(sample);
    const pointY = y(value(sample));
    if (index === 0) context.moveTo(pointX, pointY);
    else context.lineTo(pointX, pointY);
  });
}

function fillBand<Sample extends { distanceM: number }>(context: CanvasRenderingContext2D, samples: readonly Sample[], x: (sample: Sample) => number, y: (value: number) => number, upper: (sample: Sample) => number, lower: (sample: Sample) => number): void {
  context.beginPath();
  samples.forEach((sample, index) => {
    const pointX = x(sample);
    const pointY = y(upper(sample));
    if (index === 0) context.moveTo(pointX, pointY);
    else context.lineTo(pointX, pointY);
  });
  for (let index = samples.length - 1; index >= 0; index -= 1) {
    const sample = samples[index];
    if (sample) context.lineTo(x(sample), y(lower(sample)));
  }
  context.closePath();
}

function clearancePercentLabel(value: number, fresnelRadiusM: number): string {
  return fresnelRadiusM < 0.001 || !Number.isFinite(value) ? '—' : `${Math.round(value)}%`;
}

type EndpointKey = 'a' | 'b';
type DragAxis = 'pending' | 'horizontal' | 'vertical';

interface DragState {
  endpoint: EndpointKey;
  pointerId: number;
  originX: number;
  originY: number;
  initialDistanceM: number;
  initialHeightM: number;
  axis: DragAxis;
}

interface HoverState {
  index: number;
  x: number;
  tooltipY: number;
  surfaceY: number;
}

export interface ProfileHoverPosition {
  distanceM: number;
  totalDistanceM: number;
}

export function ProfileCanvas({ diagnostics, terrainProfile, endpoints, loading, terrainRetained = false, emptyMessage = null, onHoverChange, onDragStart, onEndpointChange, onDragEnd }: {
  diagnostics: LosDiagnostics | null;
  terrainProfile?: ElevationProfile | null;
  endpoints: [DesignSite, DesignSite] | null;
  loading?: boolean;
  terrainRetained?: boolean;
  emptyMessage?: string | null;
  onHoverChange?(position: ProfileHoverPosition | null): void;
  onDragStart?(): void;
  onEndpointChange?(endpoint: EndpointKey, change: { distanceM?: number; antennaHeightM?: number }): void;
  onDragEnd?(): void;
}): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const profileScaleRef = useRef<ProfileScale | null>(null);
  const publishedHoverRef = useRef<{ index: number; distanceM: number; totalDistanceM: number } | null>(null);
  const onHoverChangeRef = useRef(onHoverChange);
  const [handleTops, setHandleTops] = useState<[number, number]>([46, 40]);
  const [hover, setHover] = useState<HoverState | null>(null);
  const endpointAHeightM = endpoints?.[0].antennaHeightM ?? null;
  const endpointBHeightM = endpoints?.[1].antennaHeightM ?? null;
  const endpointALat = endpoints?.[0].coordinates.lat ?? null;
  const endpointALon = endpoints?.[0].coordinates.lon ?? null;
  const endpointBLat = endpoints?.[1].coordinates.lat ?? null;
  const endpointBLon = endpoints?.[1].coordinates.lon ?? null;
  const endpointDistanceM = endpoints ? haversineDistanceM(endpoints[0].coordinates, endpoints[1].coordinates) : 0;
  const hasEngineeringProfile = Boolean(diagnostics?.profile && diagnostics.profile.length >= 2);
  const hasCompleteTerrain = hasEngineeringProfile || Boolean(terrainProfile && !terrainProfile.partial && terrainProfile.samples.length >= 2);
  onHoverChangeRef.current = onHoverChange;

  const clearHover = (): void => {
    setHover(null);
    if (publishedHoverRef.current) {
      publishedHoverRef.current = null;
      onHoverChangeRef.current?.(null);
    }
  };

  useEffect(() => () => onHoverChangeRef.current?.(null), []);

  useEffect(() => {
    setHover(null);
    if (!publishedHoverRef.current) return;
    publishedHoverRef.current = null;
    onHoverChangeRef.current?.(null);
  }, [diagnostics, terrainProfile, endpointALat, endpointALon, endpointBLat, endpointBLon]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = (): void => {
      const rectangle = canvas.getBoundingClientRect();
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(rectangle.width * pixelRatio));
      canvas.height = Math.max(1, Math.round(rectangle.height * pixelRatio));
      const context = canvas.getContext('2d');
      if (!context) return;
      context.scale(pixelRatio, pixelRatio);
      context.clearRect(0, 0, rectangle.width, rectangle.height);
      const engineeringSamples = diagnostics?.profile && diagnostics.profile.length >= 2 ? diagnostics.profile : null;
      const rawTerrainSamples = terrainProfile && !terrainProfile.partial && terrainProfile.samples.length >= 2 ? terrainProfile.samples : null;
      const terrainSamples = hasCompleteTerrain ? engineeringSamples ?? rawTerrainSamples : null;
      if (!terrainSamples) {
        profileScaleRef.current = null;
        return;
      }
      const totalDistance = Math.max(1, endpointDistanceM, terrainSamples.at(-1)?.distanceM ?? 0);
      const terrainElevation = (sample: (typeof terrainSamples)[number]): number => 'correctedSurfaceM' in sample ? sample.correctedSurfaceM : sample.elevationM;
      const lowerValues = engineeringSamples
        ? engineeringSamples.flatMap((sample) => [sample.correctedSurfaceM, sample.fresnel100LowerM])
        : terrainSamples.map(terrainElevation);
      const endpointTops = endpoints && !engineeringSamples
        ? [terrainElevation(terrainSamples[0]!) + endpoints[0].antennaHeightM, terrainElevation(terrainSamples.at(-1)!) + endpoints[1].antennaHeightM]
        : [];
      const upperValues = engineeringSamples
        ? engineeringSamples.flatMap((sample) => [sample.correctedSurfaceM, sample.lineOfSightElevationM + sample.fresnelRadiusM])
        : [...terrainSamples.map(terrainElevation), ...endpointTops];
      const scale = createProfileScale(rectangle.width, rectangle.height, totalDistance, [...lowerValues, ...upperValues]);
      profileScaleRef.current = scale;
      const x = (sample: { distanceM: number }): number => profileXAtDistance(sample.distanceM, scale);
      const y = (value: number): number => profileYAtElevation(value, scale);

      context.save();
      context.font = '8.5px Inter, system-ui, sans-serif';
      context.textAlign = 'right';
      context.textBaseline = 'middle';
      for (const value of profileTickValues(scale, 4)) {
        const lineY = y(value);
        context.beginPath();
        context.moveTo(scale.left, lineY);
        context.lineTo(scale.right + 6, lineY);
        context.strokeStyle = 'rgba(91, 112, 132, 0.09)';
        context.lineWidth = 1;
        context.stroke();
        context.fillStyle = '#718294';
        context.fillText(`${Math.round(value)} м`, rectangle.width - 6, lineY);
      }
      context.restore();

      drawPath(context, terrainSamples, x, y, terrainElevation);
      context.lineTo(x(terrainSamples.at(-1)!), scale.bottom);
      context.lineTo(x(terrainSamples[0]!), scale.bottom);
      context.closePath();
      const terrainGradient = context.createLinearGradient(0, scale.top, 0, scale.bottom);
      terrainGradient.addColorStop(0, '#bdc8d2');
      terrainGradient.addColorStop(1, '#acb9c5');
      context.fillStyle = terrainGradient;
      context.fill();
      drawPath(context, terrainSamples, x, y, terrainElevation);
      context.strokeStyle = '#8e9ba8';
      context.lineWidth = 0.9;
      context.stroke();

      if (engineeringSamples) {
        fillBand(
          context,
          engineeringSamples,
          x,
          y,
          (sample) => sample.lineOfSightElevationM + sample.fresnelRadiusM,
          (sample) => sample.fresnel100LowerM,
        );
        context.fillStyle = 'rgba(64, 151, 235, 0.045)';
        context.fill();

        context.strokeStyle = 'rgba(53, 151, 244, 0.72)';
        context.lineWidth = 1.05;
        drawPath(context, engineeringSamples, x, y, (sample) => sample.lineOfSightElevationM + sample.fresnelRadiusM);
        context.stroke();
        drawPath(context, engineeringSamples, x, y, (sample) => sample.fresnel100LowerM);
        context.stroke();

        context.setLineDash([4, 3]);
        context.strokeStyle = 'rgba(74, 161, 244, 0.64)';
        context.lineWidth = 0.9;
        drawPath(context, engineeringSamples, x, y, (sample) => sample.lineOfSightElevationM + sample.fresnelRadiusM * 0.6);
        context.stroke();
        drawPath(context, engineeringSamples, x, y, (sample) => sample.fresnel60LowerM);
        context.stroke();
        context.setLineDash([]);

        drawPath(context, engineeringSamples, x, y, (sample) => sample.lineOfSightElevationM);
        context.strokeStyle = diagnostics?.visible === false ? '#c43e42' : '#1687f8';
        context.lineWidth = 1.2;
        context.stroke();
      }

      const first = engineeringSamples?.[0];
      const last = engineeringSamples?.at(-1);
      const endpointSurfaceTops: [number, number] | null = !hasCompleteTerrain ? null : first && last
        ? [first.lineOfSightElevationM, last.lineOfSightElevationM]
        : endpoints ? [terrainElevation(terrainSamples[0]!) + endpoints[0].antennaHeightM, terrainElevation(terrainSamples.at(-1)!) + endpoints[1].antennaHeightM] : null;
      const endpointSurfaceBases: [number, number] | null = !hasCompleteTerrain ? null : first && last
        ? [terrainElevation(terrainSamples[0]!), terrainElevation(terrainSamples.at(-1)!)]
        : endpoints ? [terrainElevation(terrainSamples[0]!), terrainElevation(terrainSamples.at(-1)!)] : null;
      if (endpointSurfaceTops && endpointSurfaceBases) {
        const endpointDistances: [number, number] = [0, totalDistance];
        for (let index = 0; index < 2; index += 1) {
          const sample = { distanceM: endpointDistances[index]! };
          context.beginPath();
          context.moveTo(x(sample), y(endpointSurfaceBases[index]!));
          context.lineTo(x(sample), y(endpointSurfaceTops[index]!));
          context.strokeStyle = '#738495';
          context.lineWidth = 1;
          context.stroke();
        }
      }

      if (engineeringSamples && diagnostics) {
        const warningStep = Math.max(1, Math.floor(engineeringSamples.length / 85));
        engineeringSamples.forEach((sample, index) => {
          if (index % warningStep !== 0 || sample.fresnelRadiusM < 0.001 || !Number.isFinite(sample.fresnelClearancePercent) || sample.fresnelClearancePercent >= diagnostics.requiredClearancePercent) return;
          context.beginPath();
          context.arc(x(sample), y(sample.correctedSurfaceM) - 1.5, sample.losClearanceM < 0 ? 2.1 : 1.65, 0, Math.PI * 2);
          context.fillStyle = sample.losClearanceM < 0 ? '#c87932' : '#d99a35';
          context.fill();
        });
        const criticalIndex = nearestProfileSampleIndex(engineeringSamples, diagnostics.criticalPoint.distanceM);
        const criticalSample = engineeringSamples[criticalIndex];
        if (criticalSample && criticalSample.fresnelClearancePercent < diagnostics.requiredClearancePercent) {
          const criticalX = x(criticalSample);
          const criticalY = y(criticalSample.correctedSurfaceM) - 1.5;
          context.beginPath();
          context.arc(criticalX, criticalY, 4.2, 0, Math.PI * 2);
          context.strokeStyle = 'rgba(198, 119, 43, 0.5)';
          context.lineWidth = 1;
          context.stroke();
          context.beginPath();
          context.arc(criticalX, criticalY, 2, 0, Math.PI * 2);
          context.fillStyle = '#c87932';
          context.fill();
        }
      }

      if (endpointSurfaceTops) {
        const nextTops: [number, number] = [
          Math.max(5, Math.min(92, y(endpointSurfaceTops[0]) / rectangle.height * 100)),
          Math.max(5, Math.min(92, y(endpointSurfaceTops[1]) / rectangle.height * 100)),
        ];
        setHandleTops((current) => Math.abs(current[0] - nextTops[0]) > 0.1 || Math.abs(current[1] - nextTops[1]) > 0.1 ? nextTops : current);
      }
    };
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    draw();
    return () => observer.disconnect();
  }, [diagnostics, terrainProfile, endpointAHeightM, endpointBHeightM, endpointDistanceM, endpoints, hasCompleteTerrain]);

  const startDrag = (endpoint: EndpointKey, event: ReactPointerEvent<HTMLButtonElement>): void => {
    if (!endpoints) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      endpoint,
      pointerId: event.pointerId,
      originX: event.clientX,
      originY: event.clientY,
      initialDistanceM: haversineDistanceM(endpoints[0].coordinates, endpoints[1].coordinates),
      initialHeightM: endpoint === 'a' ? endpoints[0].antennaHeightM : endpoints[1].antennaHeightM,
      axis: 'pending',
    };
    clearHover();
    onDragStart?.();
  };

  const movePointer = (event: ReactPointerEvent<HTMLElement>): void => {
    const drag = dragRef.current;
    const stage = stageRef.current;
    if (!stage) return;
    if (!drag) {
      const samples = diagnostics?.profile && diagnostics.profile.length >= 2 ? diagnostics.profile : terrainProfile?.samples;
      const scale = profileScaleRef.current;
      if (!samples?.length || !scale) return;
      const rectangle = stage.getBoundingClientRect();
      const target = profileHoverTarget(samples, event.clientX - rectangle.left, scale);
      const sample = samples[target.index];
      if (!sample) return;
      const surfaceElevationM = 'correctedSurfaceM' in sample ? sample.correctedSurfaceM : sample.elevationM;
      setHover({
        index: target.index,
        x: target.x,
        tooltipY: event.clientY - rectangle.top,
        surfaceY: profileYAtElevation(surfaceElevationM, scale),
      });
      const totalDistanceM = Math.max(0, samples.at(-1)?.distanceM ?? endpointDistanceM);
      const published = publishedHoverRef.current;
      if (!published || published.index !== target.index || published.distanceM !== sample.distanceM || published.totalDistanceM !== totalDistanceM) {
        publishedHoverRef.current = { index: target.index, distanceM: sample.distanceM, totalDistanceM };
        onHoverChangeRef.current?.({ distanceM: sample.distanceM, totalDistanceM });
      }
      return;
    }
    if (drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.originX;
    const deltaY = event.clientY - drag.originY;
    if (drag.axis === 'pending' && Math.hypot(deltaX, deltaY) >= 4) drag.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    if (drag.axis === 'horizontal') {
      const usableWidth = Math.max(120, stage.getBoundingClientRect().width - 78);
      const signedDelta = drag.endpoint === 'a' ? -deltaX : deltaX;
      onEndpointChange?.(drag.endpoint, { distanceM: Math.max(10, drag.initialDistanceM + signedDelta / usableWidth * drag.initialDistanceM) });
    } else if (drag.axis === 'vertical') {
      const metresPerPixel = Math.max(0.08, drag.initialDistanceM / 25_000);
      onEndpointChange?.(drag.endpoint, { antennaHeightM: Math.max(1, Math.min(300, drag.initialHeightM - deltaY * metresPerPixel)) });
    }
  };

  const endDrag = (event: ReactPointerEvent<HTMLElement>): void => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    onDragEnd?.();
  };

  const hoverEngineeringSample = hover ? diagnostics?.profile[hover.index] : null;
  const hoverTerrainSample = hover ? (hoverEngineeringSample ?? terrainProfile?.samples[hover.index]) : null;
  const hoverElevationM = hoverTerrainSample ? ('correctedSurfaceM' in hoverTerrainSample ? hoverTerrainSample.correctedSurfaceM : hoverTerrainSample.elevationM) : null;
  const fresnelMargin = hoverEngineeringSample ? hoverEngineeringSample.fresnel100LowerM - hoverEngineeringSample.correctedSurfaceM : null;
  const tooltipPlacement = hover
    ? profileTooltipPlacement(hover.x, hover.tooltipY, stageRef.current?.clientWidth ?? 0, stageRef.current?.clientHeight ?? 0)
    : null;
  return <div className="profile-canvas-stage" ref={stageRef} onPointerMove={movePointer} onPointerLeave={() => { if (!dragRef.current) clearHover(); }} onPointerUp={endDrag} onPointerCancel={endDrag}>
    <canvas className="profile-canvas" ref={canvasRef} aria-label="Редактируемый график рельефа, LOS и первой зоны Френеля" />
    {endpoints && hasCompleteTerrain ? <>
      <button className="profile-endpoint-handle a" style={{ top: `${handleTops[0]}%` }} type="button" aria-label="Перетащить точку A: по горизонтали — расстояние, по вертикали — высота" onPointerDown={(event) => startDrag('a', event)}>A</button>
      <button className="profile-endpoint-handle b" style={{ top: `${handleTops[1]}%` }} type="button" aria-label="Перетащить точку B: по горизонтали — расстояние, по вертикали — высота" onPointerDown={(event) => startDrag('b', event)}>B</button>
    </> : null}
    {hover && hoverTerrainSample && tooltipPlacement ? <>
      <i className="profile-hover-guide" style={{ left: hover.x }} aria-hidden="true" />
      <i className="profile-hover-point" style={{ left: hover.x, top: hover.surfaceY }} aria-hidden="true" />
      <div className={`profile-tooltip${tooltipPlacement.onLeft ? ' left' : ''}`} style={{ left: hover.x, top: tooltipPlacement.top }}>
        <span>Расстояние <strong>{hoverTerrainSample.distanceM >= 1_000 ? `${(hoverTerrainSample.distanceM / 1_000).toFixed(2)} км` : `${Math.round(hoverTerrainSample.distanceM)} м`}</strong></span>
        <span>Высота поверхности <strong>{hoverElevationM?.toFixed(1)} м</strong></span>
        {hoverEngineeringSample ? <>
          <span>Линия визирования <strong>{hoverEngineeringSample.lineOfSightElevationM.toFixed(1)} м</strong></span>
          <span>Радиус Fresnel F1 <strong>{hoverEngineeringSample.fresnelRadiusM.toFixed(1)} м</strong></span>
          <span>Запас LOS <strong className={hoverEngineeringSample.losClearanceM < 0 ? 'negative' : ''}>{hoverEngineeringSample.losClearanceM.toFixed(1)} м</strong></span>
          <span>Очистка F1 <strong className={fresnelMargin !== null && fresnelMargin < 0 ? 'negative' : ''}>{fresnelMargin?.toFixed(1)} м · {clearancePercentLabel(hoverEngineeringSample.fresnelClearancePercent, hoverEngineeringSample.fresnelRadiusM)}</strong></span>
        </> : <span>RF <strong>{loading ? 'пересчитывается' : 'недоступен'}</strong></span>}
      </div>
    </> : null}
    {!hasCompleteTerrain && !loading && emptyMessage ? <div className="profile-empty">{emptyMessage}</div> : null}
    {loading && !terrainRetained ? <div className="profile-loading"><span />Загрузка профиля рельефа…</div> : null}
  </div>;
}
